import { supabase } from "@/infra/supabase/client";

type LogLevel = "debug" | "info" | "warn" | "error" | "critical";

interface LogEntry {
  level: LogLevel;
  category: string;
  message: string;
  context?: Record<string, unknown>;
  url?: string;
  userAgent?: string;
  sessionId?: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  critical: 4,
};

const CONSOLE_STYLES: Record<LogLevel, string> = {
  debug: "color: #9CA3AF",
  info: "color: #3B82F6",
  warn: "color: #F59E0B; font-weight: bold",
  error: "color: #EF4444; font-weight: bold",
  critical:
    "color: #FFF; background: #DC2626; padding: 2px 6px; border-radius: 3px; font-weight: bold",
};

const IS_DEV = import.meta.env.DEV;
const MIN_CONSOLE_LEVEL: LogLevel = IS_DEV ? "debug" : "warn";
const MIN_REMOTE_LEVEL: LogLevel = "error";

let sessionId: string | null = null;
function getSessionId(): string {
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  return sessionId;
}

// Buffer para enviar logs em batch
let logBuffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

async function flushLogs() {
  if (logBuffer.length === 0) return;

  const entries = [...logBuffer];
  logBuffer = [];

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "",
    };

    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-event`;

    await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(entries),
    });
  } catch {
    // Falha silenciosa - não podemos logar erro do logger
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushLogs();
  }, 3000);
}

function sendToRemote(entry: LogEntry) {
  if (LOG_LEVELS[entry.level] < LOG_LEVELS[MIN_REMOTE_LEVEL]) return;

  logBuffer.push(entry);

  // Flush imediato para critical
  if (entry.level === "critical") {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    flushLogs();
  } else {
    scheduleFlush();
  }
}

function logToConsole(entry: LogEntry) {
  if (LOG_LEVELS[entry.level] < LOG_LEVELS[MIN_CONSOLE_LEVEL]) return;

  const prefix = `[${entry.category}]`;
  const style = CONSOLE_STYLES[entry.level];

  if (entry.context && Object.keys(entry.context).length > 0) {
    console[entry.level === "critical" ? "error" : entry.level](
      `%c${prefix} ${entry.message}`,
      style,
      entry.context,
    );
  } else {
    console[entry.level === "critical" ? "error" : entry.level](
      `%c${prefix} ${entry.message}`,
      style,
    );
  }
}

function createLogEntry(
  level: LogLevel,
  category: string,
  message: string,
  context?: Record<string, unknown>,
): LogEntry {
  return {
    level,
    category,
    message,
    context,
    url: globalThis.location?.href,
    userAgent: globalThis.navigator?.userAgent,
    sessionId: getSessionId(),
  };
}

function log(
  level: LogLevel,
  category: string,
  message: string,
  context?: Record<string, unknown>,
) {
  const entry = createLogEntry(level, category, message, context);
  logToConsole(entry);
  sendToRemote(entry);
}

// Flush ao sair da página
if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener("beforeunload", () => {
    if (logBuffer.length > 0) {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-event`;
      const body = JSON.stringify(logBuffer);
      navigator.sendBeacon?.(url, body);
    }
  });
}

/**
 * Logger estruturado para a aplicação.
 *
 * Categorias sugeridas:
 * - auth: Login, logout, sessão
 * - onboarding: Fluxo de boas-vindas
 * - gamification: XP, níveis, streaks, achievements
 * - ability: Execução de habilidades (wizards)
 * - temple: Progresso de templos, tarefas
 * - navigation: Erros de rota, redirects
 * - api: Erros de Edge Functions / Supabase
 */
export const logger = {
  debug: (
    category: string,
    message: string,
    context?: Record<string, unknown>,
  ) => log("debug", category, message, context),

  info: (
    category: string,
    message: string,
    context?: Record<string, unknown>,
  ) => log("info", category, message, context),

  warn: (
    category: string,
    message: string,
    context?: Record<string, unknown>,
  ) => log("warn", category, message, context),

  error: (
    category: string,
    message: string,
    context?: Record<string, unknown>,
  ) => log("error", category, message, context),

  critical: (
    category: string,
    message: string,
    context?: Record<string, unknown>,
  ) => log("critical", category, message, context),
};
